/**
 * ForceCanvas — Obsidian-style force-directed knowledge graph on Canvas 2D.
 *
 * d3-force handles physics (node repulsion, edge attraction, collision, clustering).
 * Canvas 2D handles rendering at 60fps.
 * No SVG, no React nodes in the viz — pure pixel drawing for 500+ nodes.
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import {
  forceSimulation, forceLink, forceManyBody, forceCenter,
  forceCollide,
  type Simulation, type SimulationNodeDatum, type SimulationLinkDatum,
} from 'd3-force'
import type { GraphNode, GraphEdge } from './graphTypes'
import { NODE_THEME, EDGE_THEME } from './graphTheme'

/* ── Simulation types ────────────────────────────────────── */

interface SimNode extends SimulationNodeDatum {
  id: string
  type: string
  label: string
  metadata: Record<string, any>
  radius: number
  color: string
  connections: number
}

interface SimLink extends SimulationLinkDatum<SimNode> {
  relationship: string
  color: string
}

interface Camera { x: number; y: number; zoom: number }

/* ── Constants ───────────────────────────────────────────── */

const NODE_BASE_SIZE: Record<string, number> = {
  exception_type: 10,
  resolution_pattern: 9,
  xbrl_concept: 7,
  reference_document: 12,
  control_objective: 9,
  failure_pattern: 6,
  detection_rule: 5,
}

const CORE_RELS = new Set(['resolved_by', 'debit_account', 'credit_account'])
const STRONG_RELS = new Set([...CORE_RELS, 'cites_standard'])
const DASHED_RELS = new Set(['cites_standard', 'governed_by', 'maps_to', 'rolls_up_to', 'violates'])

// Cluster centers — nodes of the same type drift toward these positions
const TYPE_CLUSTER: Record<string, { x: number; y: number }> = {
  detection_rule:      { x: -1.0, y: -0.7 },
  exception_type:      { x: 0,    y: -0.4 },
  failure_pattern:     { x: -0.6, y: 0 },
  resolution_pattern:  { x: 0,    y: 0.4 },
  xbrl_concept:        { x: 0.8,  y: 0.3 },
  reference_document:  { x: -0.8, y: 0.7 },
  control_objective:   { x: 0.8,  y: -0.5 },
}

// Legend entries
const LEGEND = [
  { label: 'Exception Types', color: '#22D3EE' },
  { label: 'Resolution Patterns', color: '#4ADE80' },
  { label: 'XBRL Concepts', color: '#60A5FA' },
  { label: 'Standards', color: '#E2E8F0' },
  { label: 'Controls', color: '#A78BFA' },
  { label: 'Failures', color: '#F87171' },
  { label: 'Detection Rules', color: '#FACC15' },
]

function getNodeColor(node: GraphNode): string {
  if (node.type === 'xbrl_concept' && node.metadata?.statement === 'PL') {
    return NODE_THEME.xbrl_concept.altColor || '#FB923C'
  }
  const th = NODE_THEME[node.type as keyof typeof NODE_THEME]
  return th?.color || '#94A3B8'
}

function getEdgeColor(relationship: string): string {
  return EDGE_THEME[relationship]?.color || '#475569'
}

/* ── Props ───────────────────────────────────────────────── */

interface ForceCanvasProps {
  nodes: GraphNode[]
  edges: GraphEdge[]
  selectedNodeId: string | null
  onNodeClick: (nodeId: string) => void
  onNodeHover: (nodeId: string | null) => void
  onBackgroundClick: () => void
  searchQuery?: string
}

/* ── Component ───────────────────────────────────────────── */

export function ForceCanvas({
  nodes, edges, selectedNodeId, onNodeClick, onNodeHover,
  onBackgroundClick, searchQuery,
}: ForceCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null)
  const simNodesRef = useRef<SimNode[]>([])
  const simLinksRef = useRef<SimLink[]>([])
  const cameraRef = useRef<Camera>({ x: 0, y: 0, zoom: 1 })
  const hoveredRef = useRef<string | null>(null)
  const draggingRef = useRef<SimNode | null>(null)
  const panningRef = useRef(false)
  const panStartRef = useRef({ x: 0, y: 0, cx: 0, cy: 0 })
  const animRef = useRef(0)
  const [hovered, setHovered] = useState<string | null>(null)

  const adjacencyRef = useRef<Map<string, Set<string>>>(new Map())

  const isConnected = useCallback((a: string, b: string) => {
    return adjacencyRef.current.get(a)?.has(b) || false
  }, [])

  /* ── Build simulation when data changes ───────────────── */

  useEffect(() => {
    if (!nodes.length) return

    const connCounts = new Map<string, number>()
    const adj = new Map<string, Set<string>>()
    for (const e of edges) {
      connCounts.set(e.source, (connCounts.get(e.source) || 0) + 1)
      connCounts.set(e.target, (connCounts.get(e.target) || 0) + 1)
      if (!adj.has(e.source)) adj.set(e.source, new Set())
      if (!adj.has(e.target)) adj.set(e.target, new Set())
      adj.get(e.source)!.add(e.target)
      adj.get(e.target)!.add(e.source)
    }
    adjacencyRef.current = adj

    const simNodes: SimNode[] = nodes.map(n => {
      const conns = connCounts.get(n.id) || 0
      const base = NODE_BASE_SIZE[n.type] || 6
      return {
        id: n.id,
        type: n.type,
        label: n.label,
        metadata: n.metadata,
        radius: base + Math.min(conns, 25) * 1.2,
        color: getNodeColor(n),
        connections: conns,
      }
    })

    const nodeIdSet = new Set(simNodes.map(n => n.id))
    const simLinks: SimLink[] = edges
      .filter(e => nodeIdSet.has(e.source) && nodeIdSet.has(e.target))
      .map(e => ({
        source: e.source,
        target: e.target,
        relationship: e.relationship,
        color: getEdgeColor(e.relationship),
      }))

    simNodesRef.current = simNodes
    simLinksRef.current = simLinks

    simRef.current?.stop()

    // Spread factor based on node count
    const spread = Math.sqrt(simNodes.length) * 12

    const sim = forceSimulation<SimNode>(simNodes)
      .force('link', forceLink<SimNode, SimLink>(simLinks)
        .id(d => d.id)
        .distance(link => CORE_RELS.has(link.relationship) ? 60 : 120)
        .strength(link => CORE_RELS.has(link.relationship) ? 0.8 : 0.2)
      )
      .force('charge', forceManyBody<SimNode>()
        .strength(d => -200 - d.connections * 30)
        .distanceMax(600)
      )
      .force('center', forceCenter(0, 0).strength(0.01))
      .force('collide', forceCollide<SimNode>()
        .radius(d => d.radius + 15)
        .strength(0.9)
      )
      // Custom clustering force — pulls nodes toward their type's cluster center
      .force('cluster', () => {
        for (const node of simNodes) {
          const center = TYPE_CLUSTER[node.type]
          if (!center || node.x == null || node.y == null) continue
          const tx = center.x * spread
          const ty = center.y * spread
          node.vx = (node.vx || 0) + (tx - node.x) * 0.04
          node.vy = (node.vy || 0) + (ty - node.y) * 0.04
        }
      })
      .alpha(1)
      .alphaDecay(0.01)
      .velocityDecay(0.3)

    simRef.current = sim

    setTimeout(() => fitToView(), 500)

    return () => { sim.stop() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges])

  /* ── Fit to view ──────────────────────────────────────── */

  const fitToView = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !simNodesRef.current.length) return
    const rect = canvas.getBoundingClientRect()

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    for (const n of simNodesRef.current) {
      if (n.x == null || n.y == null) continue
      minX = Math.min(minX, n.x - n.radius)
      maxX = Math.max(maxX, n.x + n.radius)
      minY = Math.min(minY, n.y - n.radius)
      maxY = Math.max(maxY, n.y + n.radius)
    }
    if (!isFinite(minX)) return

    const gw = maxX - minX + 120
    const gh = maxY - minY + 120
    const cx = (minX + maxX) / 2
    const cy = (minY + maxY) / 2
    const targetZoom = Math.max(0.3, Math.min(rect.width / gw, rect.height / gh, 1.0))
    const targetX = rect.width / 2 - cx * targetZoom
    const targetY = rect.height / 2 - cy * targetZoom

    const startCam = { ...cameraRef.current }
    const duration = 300
    const startTime = performance.now()

    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1)
      const ease = t * (2 - t) // ease-out quadratic
      cameraRef.current = {
        x: startCam.x + (targetX - startCam.x) * ease,
        y: startCam.y + (targetY - startCam.y) * ease,
        zoom: startCam.zoom + (targetZoom - startCam.zoom) * ease,
      }
      if (t < 1) requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)
  }, [])

  /* ── Canvas setup + render loop ───────────────────────── */

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    let canvasW = 0
    let canvasH = 0

    function resize() {
      const rect = container!.getBoundingClientRect()
      canvasW = rect.width
      canvasH = rect.height
      canvas!.width = canvasW * dpr
      canvas!.height = canvasH * dpr
      canvas!.style.width = `${canvasW}px`
      canvas!.style.height = `${canvasH}px`
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    function render() {
      const w = canvasW
      const h = canvasH
      const cam = cameraRef.current
      const simNodes = simNodesRef.current
      const simLinks = simLinksRef.current
      const hov = hoveredRef.current
      const sel = selectedNodeId
      const q = searchQuery?.toLowerCase() || ''

      ctx!.clearRect(0, 0, w, h)
      ctx!.save()
      ctx!.translate(cam.x, cam.y)
      ctx!.scale(cam.zoom, cam.zoom)

      // ── Edges (curved, visible, colored) ───────────
      for (const link of simLinks) {
        const src = link.source as SimNode
        const tgt = link.target as SimNode
        if (src.x == null || tgt.x == null || src.y == null || tgt.y == null) continue

        const isHovEdge = hov != null && (src.id === hov || tgt.id === hov)
        const isSelEdge = sel != null && (src.id === sel || tgt.id === sel)
        const highlight = isHovEdge || isSelEdge

        // Quadratic curve with perpendicular offset
        const dx = tgt.x - src.x
        const dy = tgt.y - src.y
        const dist = Math.sqrt(dx * dx + dy * dy) || 1
        const offset = 12
        const cpX = (src.x + tgt.x) / 2 + (dy / dist) * offset
        const cpY = (src.y + tgt.y) / 2 - (dx / dist) * offset

        ctx!.beginPath()
        ctx!.moveTo(src.x, src.y)
        ctx!.quadraticCurveTo(cpX, cpY, tgt.x, tgt.y)

        // Dashed edges for certain relationships
        if (!highlight && DASHED_RELS.has(link.relationship)) {
          ctx!.setLineDash([6, 4])
        } else {
          ctx!.setLineDash([])
        }

        ctx!.strokeStyle = highlight
          ? link.color + 'DD'   // ~87% opacity when highlighted
          : link.color + '55'   // ~33% opacity normally — clearly visible
        ctx!.lineWidth = highlight
          ? 2.0
          : STRONG_RELS.has(link.relationship) ? 1.2 : 0.8
        ctx!.stroke()
        ctx!.setLineDash([])
      }

      // ── Nodes ──────────────────────────────
      for (const node of simNodes) {
        if (node.x == null || node.y == null) continue

        const isSel = sel === node.id
        const isHov = hov === node.id
        const isConnHov = hov ? isConnected(node.id, hov) : false
        const isDimmed = (hov != null && !isHov && !isConnHov) ||
                         (q.length > 0 && !node.label.toLowerCase().includes(q) && !node.id.toLowerCase().includes(q))

        // Glow for selected/hovered
        if (isSel || isHov) {
          const grad = ctx!.createRadialGradient(
            node.x, node.y, node.radius,
            node.x, node.y, node.radius * 3.5,
          )
          grad.addColorStop(0, node.color + '50')
          grad.addColorStop(1, node.color + '00')
          ctx!.beginPath()
          ctx!.arc(node.x, node.y, node.radius * 3.5, 0, Math.PI * 2)
          ctx!.fillStyle = grad
          ctx!.fill()
        }

        // Circle fill
        ctx!.beginPath()
        ctx!.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
        ctx!.fillStyle = isDimmed ? node.color + '12' : node.color + '60'
        ctx!.fill()

        // Circle border
        ctx!.strokeStyle = isSel ? node.color : isDimmed ? node.color + '20' : node.color + '90'
        ctx!.lineWidth = isSel ? 2.5 : 1.5
        ctx!.stroke()

        // ── Label with dark pill background ──────────
        const showLabel = isHov || isSel
          || node.connections > 10
          || (cam.zoom > 0.6 && node.connections > 4)
          || cam.zoom > 1.0
        if (showLabel) {
          const baseFontSize = node.connections > 10 ? 13
            : node.connections > 4 ? 12
            : 11
          const fs = baseFontSize
          ctx!.font = `${isSel ? 'bold ' : ''}${fs}px Inter, system-ui, sans-serif`
          ctx!.textAlign = 'center'
          ctx!.textBaseline = 'top'

          let lbl = node.label
          const maxW = Math.max(80, 130)
          if (ctx!.measureText(lbl).width > maxW) {
            while (ctx!.measureText(lbl + '...').width > maxW && lbl.length > 3) {
              lbl = lbl.slice(0, -1)
            }
            lbl += '...'
          }

          const textY = node.y + node.radius + 5
          const textW = ctx!.measureText(lbl).width
          const pad = 3

          // Dark pill behind label
          if (!isDimmed) {
            ctx!.fillStyle = 'rgba(10, 22, 40, 0.8)'
            const rx = node.x - textW / 2 - pad
            const ry = textY - pad
            const rw = textW + pad * 2
            const rh = fs + pad * 2
            ctx!.beginPath()
            ctx!.roundRect(rx, ry, rw, rh, 3)
            ctx!.fill()
          }

          ctx!.fillStyle = isDimmed ? '#ffffff18' : '#E2E8F0'
          ctx!.fillText(lbl, node.x, textY)
        }
      }

      ctx!.restore()

      // ── Legend (fixed position, outside camera transform) ──
      renderLegend(ctx!, w, h)

      animRef.current = requestAnimationFrame(render)
    }

    animRef.current = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(animRef.current)
      window.removeEventListener('resize', resize)
    }
  }, [selectedNodeId, searchQuery, isConnected])

  /* ── Legend renderer ──────────────────────────────────── */

  function renderLegend(ctx: CanvasRenderingContext2D, w: number, h: number) {
    const lx = 16
    const ly = h - 190
    const lw = 175
    const lh = 178

    // Background
    ctx.fillStyle = 'rgba(10, 22, 40, 0.88)'
    ctx.beginPath()
    ctx.roundRect(lx, ly, lw, lh, 8)
    ctx.fill()
    ctx.strokeStyle = 'rgba(0,0,0,0.06)'
    ctx.lineWidth = 1
    ctx.stroke()

    // Title
    ctx.font = 'bold 11px Inter, system-ui, sans-serif'
    ctx.fillStyle = '#94A3B8'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.fillText('Knowledge Graph', lx + 12, ly + 12)

    // Entries
    ctx.font = '10px Inter, system-ui, sans-serif'
    LEGEND.forEach((item, i) => {
      const ey = ly + 34 + i * 20
      ctx.beginPath()
      ctx.arc(lx + 18, ey, 5, 0, Math.PI * 2)
      ctx.fillStyle = item.color
      ctx.fill()
      ctx.fillStyle = '#CBD5E1'
      ctx.textBaseline = 'middle'
      ctx.fillText(item.label, lx + 30, ey)
    })
  }

  /* ── Hit detection ────────────────────────────────────── */

  const findNodeAt = useCallback((mx: number, my: number): SimNode | null => {
    const cam = cameraRef.current
    const sx = (mx - cam.x) / cam.zoom
    const sy = (my - cam.y) / cam.zoom
    for (let i = simNodesRef.current.length - 1; i >= 0; i--) {
      const n = simNodesRef.current[i]
      if (n.x == null || n.y == null) continue
      const dx = sx - n.x, dy = sy - n.y
      if (dx * dx + dy * dy < (n.radius + 6) ** 2) return n
    }
    return null
  }, [])

  /* ── Mouse handlers ───────────────────────────────────── */

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top

    if (draggingRef.current) {
      const cam = cameraRef.current
      draggingRef.current.fx = (mx - cam.x) / cam.zoom
      draggingRef.current.fy = (my - cam.y) / cam.zoom
      return
    }

    if (panningRef.current) {
      const ps = panStartRef.current
      cameraRef.current = {
        ...cameraRef.current,
        x: ps.cx + (mx - ps.x),
        y: ps.cy + (my - ps.y),
      }
      return
    }

    const node = findNodeAt(mx, my)
    const id = node?.id || null
    if (id !== hoveredRef.current) {
      hoveredRef.current = id
      setHovered(id)
      onNodeHover(id)
    }
  }, [findNodeAt, onNodeHover])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top

    const node = findNodeAt(mx, my)
    if (node) {
      draggingRef.current = node
      node.fx = node.x
      node.fy = node.y
      simRef.current?.alphaTarget(0.3).restart()
    } else {
      panningRef.current = true
      panStartRef.current = { x: mx, y: my, cx: cameraRef.current.x, cy: cameraRef.current.y }
    }
  }, [findNodeAt])

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (draggingRef.current) {
      const node = draggingRef.current
      const rect = canvasRef.current!.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const cam = cameraRef.current
      const sx = (mx - cam.x) / cam.zoom
      const sy = (my - cam.y) / cam.zoom
      const dx = sx - (node.fx || 0)
      const dy = sy - (node.fy || 0)
      if (dx * dx + dy * dy < 4) {
        onNodeClick(node.id)
      }
      node.fx = null
      node.fy = null
      draggingRef.current = null
      simRef.current?.alphaTarget(0)
    } else if (panningRef.current) {
      const ps = panStartRef.current
      const rect = canvasRef.current!.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const moved = Math.abs(mx - ps.x) + Math.abs(my - ps.y)
      if (moved < 5) onBackgroundClick()
      panningRef.current = false
    }
  }, [onNodeClick, onBackgroundClick])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const cam = cameraRef.current
    const factor = e.deltaY > 0 ? 0.9 : 1.1
    const newZoom = Math.max(0.08, Math.min(4, cam.zoom * factor))
    const rect = canvasRef.current!.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top
    cameraRef.current = {
      x: mx - (mx - cam.x) * (newZoom / cam.zoom),
      y: my - (my - cam.y) * (newZoom / cam.zoom),
      zoom: newZoom,
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    hoveredRef.current = null
    setHovered(null)
    onNodeHover(null)
    if (draggingRef.current) {
      draggingRef.current.fx = null
      draggingRef.current.fy = null
      draggingRef.current = null
      simRef.current?.alphaTarget(0)
    }
    panningRef.current = false
  }, [onNodeHover])

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%', height: '100%',
        background: 'var(--bg-base, var(--bg-base))',
        cursor: hovered ? 'pointer' : panningRef.current ? 'grabbing' : 'grab',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block' }}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      />
    </div>
  )
}
